import React, { FC, useCallback, useMemo } from 'react';

import { EvFilters, IFilterValues } from '@evinced-private/ui-common';

import omit from 'lodash-es/omit';
import { DEFAULT_UNIT_ID_DISPLAY_NAME } from 'src/common/consts/platformConfigConsts';
import { DEFAULT_PERIOD_OPTION } from 'src/common/helpers/DataFilterHelper';
import { flattenObject } from 'src/common/helpers/ObjectsHelper';

import { useWebSdkTestsFilters } from '../../hooks/useWebSdkTestsFilters';
import {
	FilterSectionV2Props,
	IExtendedFilterOptions,
	V2Filters
} from '../../types/filter-section-v2';
import { FilterSectionCommon } from '../filter-section/shared/FilterSectionCommon';

import { useWebSDKFiltersStore } from './store';

export const FilterSectionV2: FC<FilterSectionV2Props> = ({
	isFilterOptionsLoading,
	filterOptions,
	filters,
	showRecentTest,
	onShowRecentTestChange,
	showTestPath,
	unitIdDisplayName = DEFAULT_UNIT_ID_DISPLAY_NAME,
	onFilterChange
}) => {
	// Only subscribe to isSelectAllLabels - access other values via getState() to avoid re-render loops
	const { isSelectAllLabels } = useWebSDKFiltersStore();

	const handleApply = useCallback(
		(appliedFilters: IFilterValues): void => {
			const flatFilters = flattenObject<IExtendedFilterOptions>(appliedFilters) as V2Filters;
			const { labels: flatLabels, testPeriod, ...rest } = flatFilters;
			const { setLabelsSearchTerm, setSelectedLabels } = useWebSDKFiltersStore.getState();

			// Determine labels to send
			// When "Select All" is active, always send undefined (all labels)
			let labels = flatLabels;
			if (isSelectAllLabels) {
				labels = undefined;
				setLabelsSearchTerm('');
				setSelectedLabels([]);
			}

			onFilterChange?.({
				...rest,
				labels,
				testPeriod: testPeriod ?? [DEFAULT_PERIOD_OPTION]
			});
		},
		[isSelectAllLabels, onFilterChange]
	);

	const initialValues = useMemo(
		() => ({
			text: { searchValue: filters?.searchValue },
			select: omit(filters, 'searchValue')
		}),
		[filters]
	);

	const {
		filterDefinitions,
		mergedFilterOptions,
		isLoading: isLabelsLoading
	} = useWebSdkTestsFilters({
		filterOptions,
		selectedLabels: filters?.labels,
		unitIdDisplayName,
		isV2: true
	});

	const isLoading = useMemo(
		() => isFilterOptionsLoading || isLabelsLoading,
		[isFilterOptionsLoading, isLabelsLoading]
	);

	return (
		<FilterSectionCommon
			isFilterOptionsLoading={isLoading}
			showRecentTest={showRecentTest}
			onShowRecentTestChange={onShowRecentTestChange}
			showTestPath={showTestPath}
		>
			<EvFilters
				className="data-filters filter-section-v2"
				onApply={handleApply}
				onReset={handleApply}
				initialValues={initialValues}
				isFilterOptionsLoaded={!isLoading}
				filterOptions={mergedFilterOptions}
				filterDefinitions={filterDefinitions}
				shouldSyncValuesWithAvailableOptions={false}
			/>
		</FilterSectionCommon>
	);
};
